import React, { useState, useEffect } from 'react';
import api from '../services/api';

const NetworkDebugPage = () => {
  const [debugInfo, setDebugInfo] = useState({
    hostname: window.location.hostname,
    port: window.location.port,
    protocol: window.location.protocol,
    userAgent: navigator.userAgent,
    apiBaseUrl: '',
    apiTestResult: '',
    networkTest: '',
  });

  useEffect(() => {
    // Get API base URL from the api service
    setDebugInfo(prev => ({
      ...prev,
      apiBaseUrl: api.defaults.baseURL
    }));

    // Test API connectivity
    const testAPI = async () => {
      try {
        console.log('Testing API connection to:', api.defaults.baseURL);
        const response = await api.get('/health');
        setDebugInfo(prev => ({
          ...prev,
          apiTestResult: `✅ Success: ${JSON.stringify(response.data)}`
        }));
      } catch (error) {
        console.error('API Test Error:', error);
        setDebugInfo(prev => ({
          ...prev,
          apiTestResult: `❌ Failed: ${error.message}`
        }));
      }
    };

    // Test login endpoint specifically
    const testLogin = async () => {
      try {
        const testCredentials = {
          email: 'test@example.com',
          password: 'test123'
        };
        
        const response = await api.post('/auth/login', testCredentials);
        setDebugInfo(prev => ({
          ...prev,
          networkTest: `✅ Login endpoint reachable: ${response.status}`
        }));
      } catch (error) {
        if (error.response) {
          // The request was made and the server responded with a status code
          setDebugInfo(prev => ({
            ...prev,
            networkTest: `✅ Login endpoint reachable, returned: ${error.response.status} - ${error.response.data?.detail || 'Authentication failed (expected)'}`
          }));
        } else if (error.request) {
          // The request was made but no response was received
          setDebugInfo(prev => ({
            ...prev,
            networkTest: `❌ Network error - cannot reach login endpoint: ${error.message}`
          }));
        } else {
          setDebugInfo(prev => ({
            ...prev,
            networkTest: `❌ Request setup error: ${error.message}`
          }));
        }
      }
    };

    testAPI();
    setTimeout(testLogin, 1000);
  }, []);

  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

  return (
    <div className="min-h-screen bg-gray-100 py-6">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-md p-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-6">
            🔍 Network Debug Information
          </h1>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Device Information */}
            <div className="bg-blue-50 p-4 rounded-lg">
              <h2 className="text-lg font-semibold text-blue-800 mb-3">
                📱 Device Information
              </h2>
              <div className="space-y-2 text-sm">
                <div>
                  <strong>Device Type:</strong> {isMobile ? '📱 Mobile' : '💻 Desktop'}
                </div>
                <div>
                  <strong>Hostname:</strong> {debugInfo.hostname}
                </div>
                <div>
                  <strong>Port:</strong> {debugInfo.port || 'default'}
                </div>
                <div>
                  <strong>Protocol:</strong> {debugInfo.protocol}
                </div>
                <div>
                  <strong>Full URL:</strong> {window.location.href}
                </div>
              </div>
            </div>

            {/* API Configuration */}
            <div className="bg-green-50 p-4 rounded-lg">
              <h2 className="text-lg font-semibold text-green-800 mb-3">
                🔗 API Configuration
              </h2>
              <div className="space-y-2 text-sm">
                <div>
                  <strong>API Base URL:</strong> 
                  <br />
                  <code className="bg-gray-100 px-2 py-1 rounded text-xs">
                    {debugInfo.apiBaseUrl}
                  </code>
                </div>
                <div>
                  <strong>Expected for Mobile:</strong>
                  <br />
                  <code className="bg-gray-100 px-2 py-1 rounded text-xs">
                    http://192.168.1.4:8001
                  </code>
                </div>
              </div>
            </div>

            {/* API Tests */}
            <div className="bg-yellow-50 p-4 rounded-lg">
              <h2 className="text-lg font-semibold text-yellow-800 mb-3">
                🧪 API Health Test
              </h2>
              <div className="text-sm">
                {debugInfo.apiTestResult || '⏳ Testing...'}
              </div>
            </div>

            {/* Network Tests */}
            <div className="bg-purple-50 p-4 rounded-lg">
              <h2 className="text-lg font-semibold text-purple-800 mb-3">
                🌐 Login Endpoint Test
              </h2>
              <div className="text-sm">
                {debugInfo.networkTest || '⏳ Testing...'}
              </div>
            </div>
          </div>

          {/* Troubleshooting Tips */}
          <div className="mt-6 bg-red-50 p-4 rounded-lg">
            <h2 className="text-lg font-semibold text-red-800 mb-3">
              🚨 Mobile Login Issues - Troubleshooting
            </h2>
            <div className="text-sm space-y-2">
              <div>
                <strong>If you're on mobile and login fails:</strong>
              </div>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Check that you're accessing via <code>http://192.168.1.4:3000</code></li>
                <li>Make sure both devices are on the same WiFi network</li>
                <li>The API should auto-detect and use <code>http://192.168.1.4:8001</code></li>
                <li>If you see "localhost" in API Base URL above, that's the problem</li>
                <li>Try refreshing the page to reload the API configuration</li>
              </ul>
              
              {isMobile && debugInfo.hostname !== '192.168.1.4' && (
                <div className="mt-3 p-3 bg-red-100 border border-red-300 rounded">
                  <strong>❌ Issue Detected:</strong> You're on mobile but not accessing via the correct network IP. 
                  <br />
                  Try: <a href="http://192.168.1.4:3000" className="text-blue-600 underline">
                    http://192.168.1.4:3000
                  </a>
                </div>
              )}
              
              {isMobile && debugInfo.hostname === '192.168.1.4' && debugInfo.apiBaseUrl.includes('localhost') && (
                <div className="mt-3 p-3 bg-red-100 border border-red-300 rounded">
                  <strong>❌ API Configuration Issue:</strong> API is still pointing to localhost instead of network IP.
                  Try refreshing the page.
                </div>
              )}
              
              {isMobile && debugInfo.hostname === '192.168.1.4' && debugInfo.apiBaseUrl.includes('192.168.1.4') && (
                <div className="mt-3 p-3 bg-green-100 border border-green-300 rounded">
                  <strong>✅ Configuration looks correct!</strong> API should work properly on mobile.
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="mt-6 flex flex-wrap gap-3">
            <button
              onClick={() => window.location.reload()}
              className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
            >
              🔄 Refresh Page
            </button>
            <a
              href="/"
              className="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded inline-block"
            >
              🏠 Go to Login
            </a>
            <a
              href="/check_in"
              className="bg-purple-500 hover:bg-purple-700 text-white font-bold py-2 px-4 rounded inline-block"
            >
              📱 Go to Check-in
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NetworkDebugPage;
